/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */

import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Tabs, Tab, TabList, TabPanels, TabPanel } from '@carbon/react';
import { Switch, Route, withRouter, useRouteMatch } from 'react-router-dom';
import classNames from 'classnames';
import { useApi } from 'contexts/ApiHandler';
import { useConfig } from 'contexts/config';

import Overview from './Overview';
import TestToolGraphQL from './TestToolGraphQL';

function GraphQLDisplay({ infoSectionEntries, documentationEntries, apps }) {
  const { api, apiHandler } = useApi();
  const { options } = useConfig();
  const { apiName } = useRouteMatch('/:apiName/:path?')?.params || {};
  const { t } = useTranslation();
  const isTestable = options.tryIt && apiHandler.isTestable(api);

  const GraphQL = withRouter(({ history, selectedIndex }) => (
    <Tabs className="graphql-display__section-tabs" selectedIndex={selectedIndex}>
      <TabList aria-label="graphql display tabs">
        <Tab
          id="graphql-display__section-overview-tab"
          className="graphql-display__section-overview-tab"
          onClick={() => {
            history.push(`/${apiName}/overview`);
          }}
          title={t('overview')}
        >
          {t('overview')}
        </Tab>
        {isTestable && (
          <Tab
            id="TryItTab"
            onClick={() => {
              history.push(`/${apiName}/try-it`);
            }}
          >
            {t('try')}
          </Tab>
        )}
      </TabList>
      <TabPanels>
        <TabPanel>
          <section className={classNames('tab-content', 'overview')} aria-label="graphql-overview">
            <div className="graphql-display__section-overview-tab-contents">
              <Overview infoSectionEntries={infoSectionEntries} documentationEntries={documentationEntries} />
            </div>
          </section>
        </TabPanel>
        <TabPanel>
          <TestToolGraphQL api={api} options={options} apps={apps} />
        </TabPanel>
      </TabPanels>
    </Tabs>
  ));

  return (
    <div className={classNames('apiconnect-explorer', 'graphql-display', 'main')} aria-label="graphqldisplay-main">
      <Switch>
        {isTestable && (
          <Route exact path={`/${apiName}/try-it`}>
            <GraphQLLanding>
              <GraphQL selectedIndex={1} />
            </GraphQLLanding>
          </Route>
        )}
        <Route path={`/${apiName}`}>
          <GraphQLLanding>
            <GraphQL selectedIndex={0} />
          </GraphQLLanding>
        </Route>
      </Switch>
    </div>
  );
}

function GraphQLLanding({ children }) {
  return (
    <section className="graphql-display__section" aria-label="operation-display">
      {children}
    </section>
  );
}

// needed for finding in tests
GraphQLDisplay.displayName = 'GraphQLDisplay';
GraphQLDisplay.propTypes = {
  infoSectionEntries: PropTypes.array,
  documentationEntries: PropTypes.array,
  apps: PropTypes.array
};

export default GraphQLDisplay;
